import { useState, useEffect } from 'react';
import { axios } from 'ApiClient';

// Fetches the change history of one document when a modal opens, instead of reading it off the
// table row. The rows used to carry every history array of every record so that a modal which is
// rarely opened could render — and, because the actor's name was only a reference, an AdminUser was
// populated per entry per row on top of that.
//
// `entity` is the model the history is recorded against, e.g. 'MediaContent'; the endpoint
// whitelists per entity, so it is what decides which properties may come back at all.
//
// `field` narrows the result: a string for one property, an array for several, nothing for all.
//
// The endpoint answers newest first. `oldestFirst` flips that back for the modals that rendered the
// embedded arrays in insertion order, which read oldest at the top.
const useEntityHistoryHook = ({
  url, entity, id, field, isOpen, oldestFirst = false,
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !id || !entity) {
      setRecords([]);
      return undefined;
    }
    // The modal can be closed, or switched to another property, before the request lands.
    let current = true;
    setLoading(true);
    setError(null);
    // Dropped up front, not just replaced on success: the id or the property can change while the
    // modal stays open, and holding the previous answer means rendering one entity's or one
    // property's entries under another's heading — indefinitely, if the new request fails.
    setRecords([]);

    const params = new URLSearchParams();
    params.append('entity', entity);
    params.append('id', id);
    [].concat(field || []).filter(Boolean).forEach(f => params.append('field', f));

    axios.get(`${url}?${params.toString()}`)
      .then(({ data }) => {
        if (!current) return;
        if (data?.success) setRecords(oldestFirst ? (data.records || []).slice().reverse() : (data.records || []));
        else setError(data?.error || 'Could not load the change history');
      })
      .catch(e => current && setError(e.message))
      .finally(() => current && setLoading(false));

    return () => { current = false; };
  }, [url, entity, id, isOpen, oldestFirst, JSON.stringify(field)]);

  return { records, loading, error };
};

export default useEntityHistoryHook;
